import {THUMBNAIL_SCROLL_AMOUNT} from './../../../framework/rest/utils/constants';
import {useEffect, useRef, useState, useCallback} from 'react';

interface UseThumbnailCarouselProps {
    gallery?: unknown[];
    currentSlide: number;
    onThumbClick?: (index: number) => void;
}

export const useThumbnailCarousel = ({gallery, currentSlide, onThumbClick}: UseThumbnailCarouselProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const thumbRefs = useRef<(HTMLDivElement | null)[]>([]);
    const prevRef = useRef<HTMLDivElement>(null);
    const nextRef = useRef<HTMLDivElement>(null);
    const [showArrows, setShowArrows] = useState(false);

    useEffect(() => {
        const checkOverflow = () => {
            const container = containerRef.current;
            if (!container) return;

            setShowArrows(container.scrollWidth > container.clientWidth);
        };

        checkOverflow();

        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [gallery]);

    useEffect(() => {
        const prev = prevRef.current;
        const next = nextRef.current;

        if (!prev || !next) return;

        const handlePrev = () => {
            containerRef.current?.scrollBy({
                left: -THUMBNAIL_SCROLL_AMOUNT,
                behavior: 'smooth',
            });
        };

        const handleNext = () => {
            containerRef.current?.scrollBy({
                left: THUMBNAIL_SCROLL_AMOUNT,
                behavior: 'smooth',
            });
        };

        prev.addEventListener('click', handlePrev);
        next.addEventListener('click', handleNext);

        return () => {
            prev.removeEventListener('click', handlePrev);
            next.removeEventListener('click', handleNext);
        };
    }, [showArrows]);

    useEffect(() => {
        const activeThumb = thumbRefs.current[currentSlide];

        activeThumb?.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest',
        });
    }, [currentSlide]);

    const setThumbRef = useCallback(
        (index: number) => (el: HTMLDivElement | null) => {
            thumbRefs.current[index] = el;
        },
        [],
    );

    const handleThumbClick = useCallback(
        (index: number) => {
            onThumbClick?.(index);
        },
        [onThumbClick],
    );

    return {
        containerRef,
        prevRef,
        nextRef,
        showArrows,
        setThumbRef,
        handleThumbClick,
    };
};
